import { canonicalClinicalCode, clinicalCodeCategory, clinicalCodeChapter, isCategoryInRange } from "./normalise";
export type BundlePairInfo = { daggerCode: string; asteriskCode: string };

/** Chapter C — malignant neoplasms. */
export const isMalignantNeoplasmIcd10Code = (code: string): boolean => clinicalCodeChapter(code) === "C";

/** `C77–C79` — secondary malignant neoplasms, which sit behind the primary tumour by definition. */
export const isSecondaryMalignantNeoplasmIcd10Code = (code: string): boolean => isCategoryInRange(code, "C", 77, 79);

/** Chapter R — symptoms, signs and abnormal findings not classified elsewhere. */
export const isSymptomIcd10Code = (code: string): boolean => clinicalCodeChapter(code) === "R";

/** ICD-10 categories that share a 3-character root, e.g. every `C34.*`. */
export const icd10Category = clinicalCodeCategory;

/** Chapter Z — factors influencing health status and contact with health services. */
export const isHealthStatusFactorIcd10Code = (code: string): boolean => clinicalCodeChapter(code) === "Z";

/**
 * `Z85–Z87` — personal history of a past condition. `Z88` (allergy status) is deliberately outside
 * the group so customer rules can still discard a mild-allergy code.
 */
const PERSONAL_HISTORY_CATEGORIES: ReadonlySet<string> = new Set(["Z85", "Z86", "Z87"]);

export const isPersonalHistoryIcd10Code = (code: string): boolean =>
  PERSONAL_HISTORY_CATEGORIES.has(clinicalCodeCategory(code));

/** `Z80–Z84` — family history of disease. */
export const isFamilyHistoryIcd10Code = (code: string): boolean => isCategoryInRange(code, "Z", 80, 84);

/** `Z88` — allergy status to drugs, medicaments and biological substances. */
export const isAllergyStatusIcd10Code = (code: string): boolean => clinicalCodeCategory(code) === "Z88";

/**
 * `Z89–Z94` and `Z98–Z99` — acquired absence of a limb or organ, risk-factor and treatment history,
 * artificial openings, transplant status, other post-surgical states and machine dependence.
 * `Z95–Z97` (implants and devices) sit between the two ranges and are not included.
 */
export const isOtherHistoryAndStatusIcd10Code = (code: string): boolean =>
  isCategoryInRange(code, "Z", 89, 94) || isCategoryInRange(code, "Z", 98, 99);

/** `Z80–Z99` — family and personal history and conditions influencing health status; the block DCS.XXI.21 bars from the primary position. */
export const isHistoryAndStatusIcd10Code = (code: string): boolean => isCategoryInRange(code, "Z", 80, 99);

/** `Z38.0` — singleton liveborn, the code a well-baby episode turns on. */
export const WELL_BABY_ICD10_CODE = "Z38.0";

export const isWellBabyIcd10Code = (code: string): boolean => canonicalClinicalCode(code) === WELL_BABY_ICD10_CODE;

/** `R73.0` — abnormal glucose tolerance test. A chapter-R code that carries a finding worth keeping. */
export const ABNORMAL_GLUCOSE_TOLERANCE_ICD10_CODE = "R73.0";

/**
 * `Z00–Z13` — persons encountering health services for examination and investigation: the reason
 * for the contact was to look, not a condition found. The block DCS.XXI.1 is written against.
 */
export const isExaminationEncounterIcd10Code = (code: string): boolean => isCategoryInRange(code, "Z", 0, 13);

/** `Z08–Z09` — follow-up examination after treatment, for a malignancy (`Z08`) or anything else (`Z09`). */
export const isFollowUpExaminationIcd10Code = (code: string): boolean => isCategoryInRange(code, "Z", 8, 9);

/** `Z12–Z13` — special screening examination, for neoplasms (`Z12`) or other diseases (`Z13`). */
export const isScreeningExaminationIcd10Code = (code: string): boolean => isCategoryInRange(code, "Z", 12, 13);

/** `Z03.8` — observation for other suspected diseases and conditions, the suspicion a test ruled on. */
export const OBSERVATION_FOR_SUSPECTED_CONDITION_ICD10_CODE = "Z03.8";

export const isObservationForSuspectedConditionIcd10Code = (code: string): boolean =>
  canonicalClinicalCode(code) === OBSERVATION_FOR_SUSPECTED_CONDITION_ICD10_CODE;

/** "Other and unspecified" names a catch-all category, not a vaguer version of a sibling code. */
export const isUnspecifiedIcd10Code = (code: string, description?: string): boolean => {
  if (canonicalClinicalCode(code).endsWith(".9")) return true;
  const lowered = description?.toLowerCase() ?? "";
  return lowered.includes("unspecified") && !lowered.includes("other and unspecified");
};
